// components/TimerProvider.tsx
import { useEffect, useRef, useCallback } from "react";
import { TimerState, useMenuStore } from "../store/menuState";
import { timerLog, logger } from "../utils/logger";

// 전역 타이머 관리 (중복 방지)
let globalTimerRef: NodeJS.Timeout | null = null;
let isGlobalTimerRunning = false;
let globalTimerId = 0; // 타이머 ID로 중복 방지

const TimerProvider = () => {
  const remainingTime = useMenuStore((s) => s.remainingOrderTime);
  const decrement = useMenuStore((s) => s.decrementRemainingOrderTime);
  const timerState = useMenuStore((s) => s.timerState);
  const isTimerRunning = useMenuStore((s) => s.isTimerRunning);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const isInitialMount = useRef(true);
  const lastUpdateTime = useRef(0);
  const minUpdateInterval = 1000; // 최소 1초 간격
  const currentTimerId = useRef(0);

  // 마운트 확인
  useEffect(() => {
    timerLog('[TimerProvider] Component mounted');
    return () => {
      timerLog('[TimerProvider] Component unmounted');
    };
  }, []);

  // 타이머 정리 함수
  const cleanupTimer = useCallback(() => {
    timerLog('🕐 [TimerProvider] cleanupTimer called');
    
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (globalTimerRef) {
      clearInterval(globalTimerRef);
      globalTimerRef = null;
      isGlobalTimerRunning = false;
    }

    currentTimerId.current = 0;
  }, []);

  // 타이머 시작 함수 (중복 방지 강화)
  const startTimer = useCallback(() => {
    timerLog('🕐 [TimerProvider] startTimer called, remainingTime:', remainingTime)
    
    // 이미 전역 타이머가 실행 중이면 새로 시작하지 않음
    if (isGlobalTimerRunning) {
      timerLog('🕐 [TimerProvider] Global timer already running, skipping');
      return;
    }

    // 남은 시간이 없으면 타이머 시작하지 않음
    if (remainingTime <= 0) {
      timerLog('🕐 [TimerProvider] No remaining time, skipping timer start');
      return;
    }

    // 최소 간격 확인 (화면 깜빡임 방지)
    const currentTime = Date.now();
    if (currentTime - lastUpdateTime.current < minUpdateInterval) {
      timerLog('🕐 [TimerProvider] Update too frequent, skipping');
      return;
    }

    // cleanupTimer() 제거 - 기존 타이머 유지

    // 새로운 타이머 ID 생성
    globalTimerId++;
    currentTimerId.current = globalTimerId;

    isGlobalTimerRunning = true;
    timerLog('🕐 [TimerProvider] Starting timer with ID:', globalTimerId, 'remainingTime:', remainingTime)
    
    globalTimerRef = setInterval(() => {
      // 타이머 ID가 변경되었으면 실행하지 않음 (중복 방지)
      if (currentTimerId.current !== globalTimerId) {
        timerLog('🕐 [TimerProvider] Timer ID mismatch, skipping execution');
        return;
      }

      // 화면 깜빡임 방지를 위해 최소 간격 확인
      const currentTime = Date.now();
      if (currentTime - lastUpdateTime.current < minUpdateInterval) {
        return;
      }

      // 페이지 변경 시 타이머 상태 확인
      const menuStore = useMenuStore.getState();
      if (!menuStore.isTimerRunning) {
        timerLog('🕐 [TimerProvider] Timer not running in store, skipping execution');
        return;
      }

      lastUpdateTime.current = currentTime;
      
      decrement();
    }, 60000); // 1분(60초) 단위로 변경

    timerRef.current = globalTimerRef;
  }, [decrement, remainingTime, cleanupTimer]);

  // 타이머 중지 함수
  const stopTimer = useCallback(() => {
    cleanupTimer();
  }, [cleanupTimer]);

  useEffect(() => {
    
    // 첫 마운트 시에만 실행
    if (isInitialMount.current) {
      isInitialMount.current = false;
      
      // 타이머가 실행 중이고 남은 시간이 있을 때만 interval 설정
      if (isTimerRunning && remainingTime > 0) {
        startTimer();
      }
      return;
    }

    // 이후에는 isTimerRunning 상태 변경에만 반응
    if (isTimerRunning && remainingTime > 0) {
      // 기존 타이머가 실행 중이면 새로 시작하지 않음
      if (!isGlobalTimerRunning) {
        startTimer();
      }
    } else if (!isTimerRunning) {
      stopTimer();
    }

    // cleanup에서 cleanupTimer 호출 제거 - 타이머 유지
    return () => {
      // cleanupTimer(); // 제거
    };
  }, [isTimerRunning, startTimer, stopTimer, cleanupTimer]); // remainingTime 제거

  // 컴포넌트 언마운트 시 정리
  useEffect(() => {
    return () => {
      cleanupTimer();
    };
  }, [cleanupTimer]);

  return null;
};

export default TimerProvider;
